import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import employeeService from "../../services/employeeService";

const MAX_PHOTO_SIZE = 2 * 1024 * 1024;
const EMPTY_FORM = {
  employeeCode: "",
  fullName: "",
  gender: "",
  department: "",
  designation: "",
  employeeCategory: "",
  qualification: "",
  experience: "",
  joiningDate: "",
  dateOfBirth: "",
  mobile: "",
  email: "",
  address: "",
  photoUrl: "",
  photoPublicId: "",
  photoDataUrl: "",
  status: "Active",
  notes: "",
};

const GENDER_OPTIONS = ["Male", "Female", "Other"];
const EMPLOYEE_CATEGORY_OPTIONS = ["Teaching", "Non Teaching"];
const STATUS_OPTIONS = ["Active", "Inactive", "On Leave", "Resigned"];

function EmployeeField({
  label,
  name,
  required = false,
  type = "text",
  placeholder = "",
  value,
  onChange,
  error,
}) {
  return (
    <div className="form-group">
      <label className="form-label">
        {label}
        {required && <span style={{ color: "var(--danger)" }}> *</span>}
      </label>
      <input
        className={`form-control ${error ? "error" : ""}`}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
      />
      {error && <span className="form-error">{error}</span>}
    </div>
  );
}

function EmployeeSelectField({
  label,
  name,
  options,
  required = false,
  value,
  onChange,
  error,
}) {
  return (
    <div className="form-group">
      <label className="form-label">
        {label}
        {required && <span style={{ color: "var(--danger)" }}> *</span>}
      </label>
      <select
        className={`form-control ${error ? "error" : ""}`}
        name={name}
        value={value}
        onChange={onChange}
      >
        <option value="">Select {label}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      {error && <span className="form-error">{error}</span>}
    </div>
  );
}

export default function EmployeeForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [removePhoto, setRemovePhoto] = useState(false);

  useEffect(() => {
    if (!isEdit) return;

    (async () => {
      try {
        const res = await employeeService.getById(id);
        setForm({
          employeeCode: res.data.employeeCode || "",
          fullName: res.data.fullName || "",
          gender: res.data.gender || "",
          department: res.data.department || "",
          designation: res.data.designation || "",
          employeeCategory: res.data.employeeCategory || "",
          qualification: res.data.qualification || "",
          experience: res.data.experience || "",
          joiningDate: res.data.joiningDate || "",
          dateOfBirth: res.data.dateOfBirth || "",
          mobile: res.data.mobile || "",
          email: res.data.email || "",
          address: res.data.address || "",
          photoUrl: res.data.photo?.url || "",
          photoPublicId: res.data.photo?.publicId || "",
          photoDataUrl: "",
          status: res.data.status || "Active",
          notes: res.data.notes || "",
        });
      } catch (err) {
        toast.error(err.message);
        navigate("/employees");
      } finally {
        setFetching(false);
      }
    })();
  }, [id, isEdit, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const nextErrors = {};

    if (!form.employeeCode.trim()) nextErrors.employeeCode = "Employee code is required";
    if (!form.fullName.trim()) nextErrors.fullName = "Employee name is required";
    if (!form.department.trim()) nextErrors.department = "Department is required";
    if (!form.designation.trim()) nextErrors.designation = "Designation is required";
    if (!form.employeeCategory.trim()) nextErrors.employeeCategory = "Employee category is required";
    if (!form.joiningDate.trim()) nextErrors.joiningDate = "Joining date is required";
    if (form.mobile && !/^\d{10}$/.test(form.mobile)) {
      nextErrors.mobile = "Mobile number must be 10 digits";
    }
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) {
      nextErrors.email = "Enter a valid email address";
    }

    return nextErrors;
  };

  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error("Failed to read image file."));
      reader.readAsDataURL(file);
    });

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please choose a valid image file.");
      e.target.value = "";
      return;
    }

    if (file.size > MAX_PHOTO_SIZE) {
      toast.error("Photo must be 2 MB or smaller.");
      e.target.value = "";
      return;
    }

    try {
      const dataUrl = await readFileAsDataUrl(file);
      setForm((prev) => ({
        ...prev,
        photoUrl: dataUrl,
        photoDataUrl: dataUrl,
      }));
      setRemovePhoto(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      e.target.value = "";
    }
  };

  const clearPhoto = () => {
    setForm((prev) => ({
      ...prev,
      photoUrl: "",
      photoPublicId: "",
      photoDataUrl: "",
    }));
    setRemovePhoto(Boolean(isEdit));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = validate();

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      toast.error("Please fix the errors before saving.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...form,
        removePhoto,
      };
      if (isEdit) {
        await employeeService.update(id, payload);
        toast.success("Employee updated successfully.");
      } else {
        await employeeService.create(payload);
        toast.success("Employee created successfully.");
      }
      navigate("/employees");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="card">
        <div className="loading-center">
          <div className="spinner" />
          Loading employee...
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">{isEdit ? "Edit Employee" : "Add Employee"}</h1>
          <p className="page-subtitle">
            Maintain employee profile details for management and attendance tracking.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="card">
          <div className="card-body">
            <div className="form-grid">
              <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                <label className="form-label">Employee Photo</label>
                <div style={{ display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap" }}>
                  <div
                    style={{
                      width: 110,
                      height: 130,
                      borderRadius: 12,
                      overflow: "hidden",
                      border: "1px solid var(--border)",
                      background: "#f8fafc",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "var(--text-muted)",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {form.photoUrl ? (
                      <img
                        src={form.photoUrl}
                        alt="Employee"
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <span>No Photo</span>
                    )}
                  </div>
                  <div style={{ display: "grid", gap: "0.55rem" }}>
                    <input
                      type="file"
                      accept="image/*"
                      className="form-control"
                      onChange={handlePhotoChange}
                      style={{ maxWidth: 320 }}
                    />
                    <span className="form-hint">JPG, PNG or WEBP up to 2 MB.</span>
                    {form.photoUrl && (
                      <div>
                        <button type="button" className="btn btn-outline btn-sm" onClick={clearPhoto}>
                          Remove Photo
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <EmployeeField
                label="Employee Code"
                name="employeeCode"
                required
                placeholder="e.g. EMP001"
                value={form.employeeCode}
                onChange={handleChange}
                error={errors.employeeCode}
              />
              <EmployeeField
                label="Full Name"
                name="fullName"
                required
                placeholder="e.g. Dr. Ayesha Khan"
                value={form.fullName}
                onChange={handleChange}
                error={errors.fullName}
              />
              <EmployeeSelectField
                label="Gender"
                name="gender"
                options={GENDER_OPTIONS}
                value={form.gender}
                onChange={handleChange}
                error={errors.gender}
              />
              <EmployeeSelectField
                label="Employee Category"
                name="employeeCategory"
                options={EMPLOYEE_CATEGORY_OPTIONS}
                required
                value={form.employeeCategory}
                onChange={handleChange}
                error={errors.employeeCategory}
              />
              <EmployeeSelectField
                label="Status"
                name="status"
                options={STATUS_OPTIONS}
                required
                value={form.status}
                onChange={handleChange}
                error={errors.status}
              />
              <EmployeeField
                label="Department"
                name="department"
                required
                placeholder="e.g. Administration"
                value={form.department}
                onChange={handleChange}
                error={errors.department}
              />
              <EmployeeField
                label="Designation"
                name="designation"
                required
                placeholder="e.g. Lecturer"
                value={form.designation}
                onChange={handleChange}
                error={errors.designation}
              />
              <EmployeeField
                label="Qualification"
                name="qualification"
                placeholder="e.g. M.Pharm, MBA"
                value={form.qualification}
                onChange={handleChange}
                error={errors.qualification}
              />
              <EmployeeField
                label="Experience"
                name="experience"
                placeholder="e.g. 5 Years"
                value={form.experience}
                onChange={handleChange}
                error={errors.experience}
              />
              <EmployeeField
                label="Joining Date"
                name="joiningDate"
                type="date"
                required
                value={form.joiningDate}
                onChange={handleChange}
                error={errors.joiningDate}
              />
              <EmployeeField
                label="Date of Birth"
                name="dateOfBirth"
                type="date"
                value={form.dateOfBirth}
                onChange={handleChange}
                error={errors.dateOfBirth}
              />
              <EmployeeField
                label="Mobile"
                name="mobile"
                placeholder="10-digit mobile number"
                value={form.mobile}
                onChange={handleChange}
                error={errors.mobile}
              />
              <EmployeeField
                label="Email"
                name="email"
                type="email"
                placeholder="employee@example.com"
                value={form.email}
                onChange={handleChange}
                error={errors.email}
              />
              <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                <label className="form-label">Address</label>
                <textarea
                  className="form-control"
                  rows={2}
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  placeholder="Employee address"
                  style={{ resize: "vertical" }}
                />
              </div>
              <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                <label className="form-label">Notes</label>
                <textarea
                  className="form-control"
                  rows={3}
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  placeholder="Optional notes about the employee"
                  style={{ resize: "vertical" }}
                />
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "0.5rem",
                marginTop: "1.25rem",
              }}
            >
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => navigate("/employees")}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? "Saving..." : isEdit ? "Update Employee" : "Add Employee"}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
